import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    OnDestroy,
    Output,
    QueryList,
    ViewChild,
    ViewChildren,
    ViewEncapsulation,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
    FolderTreeStateService,
    IFolderContainer,
} from '@components/ui-folder-tree/ui-folder-tree-state';
import {
    FolderType,
    IFolder,
} from '@models';

import {
    Observable,
    Subject,
} from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    switchMap,
    take,
    takeUntil,
    tap,
} from 'rxjs/operators';

@Component({
    selector: 'ui-folder-tree',
    templateUrl: 'ui-folder-tree.component.html',
    styleUrls: ['./ui-folder-tree.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class UiFolderTreeComponent implements AfterViewInit, OnDestroy, AfterViewInit {
    public readonly searchItemSize = 60;

    @ViewChild('treeContainer', { static: false })
    public treeContainer!: ElementRef;

    @Input()
    public state!: FolderTreeStateService;

    @Input()
    public context!: 'manage' | 'switch';

    @Output()
    public highlightedFolder = new EventEmitter<IFolder | null>();

    @Output()
    public selectedFolder = new EventEmitter<IFolder>();

    public FolderType = FolderType;

    public vs$!: Observable<CdkVirtualScrollViewport>;

    public vs!: CdkVirtualScrollViewport;

    @ViewChild('search', { static: false })
    public search!: ElementRef;

    public searchControl = new FormControl('');

    private _destroyed$ = new Subject();

    @HostBinding('class.ui-folder-tree-loading')
    public get isFolderLoading() {
        return this.state && this.state.loadingFolder$.value.value;
    }

    public get highlighted() {
        return this.state.highlighted$.value;
    }

    @ViewChildren(CdkVirtualScrollViewport)
    private _virtualScroller!: QueryList<CdkVirtualScrollViewport>;

    constructor(
        private _cd: ChangeDetectorRef,
    ) { }

    ngAfterViewInit() {
        this.state.highlighted$.pipe(
            takeUntil(this._destroyed$),
        ).subscribe(f => this.highlightedFolder.next(f));

        if (
            // we have folders loaded
            this.state.treeContainers.length ||
            // we don't have folders and there's nothing selected
            this.state.selectedFolderId == null
        ) {
            this.state.refresh();
        }

        // rendered virtual scroller instance
        this.vs$ = this._virtualScroller.changes.pipe(
            filter(changes => !!this.state.treeContainers.length && !!changes.first),
            take(1),
            switchMap(({ first: vs }: { first: CdkVirtualScrollViewport }) =>
                vs.scrolledIndexChange.pipe(
                    take(1),
                    tap(() => this._cd.detectChanges()),
                    map(() => vs),
                )),
        );

        this.vs$.pipe(
            tap(vs => vs.scrollToIndex(this.state.activeIndex)),
            switchMap((vs) => vs.scrolledIndexChange),
            takeUntil(this._destroyed$),
        ).subscribe(index => this.state.activeIndex = index);

        this.searchControl.valueChanges.pipe(
            debounceTime(250),
            distinctUntilChanged(),
            takeUntil(this._destroyed$),
        ).subscribe(val => {
            this.state.searchValue = val;
        });

        this._virtualScroller.changes.pipe(
            tap(({ first: vs }) => this.vs = vs),
            takeUntil(this._destroyed$),
        ).subscribe();
    }

    ngOnDestroy() {
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    public trackById = (_: number, folder: IFolderContainer) =>
        folder.folderData ? folder.folderData.id : folder.fakeId

    public selectFolder(folder: IFolder | undefined) {
        if (!folder) { return; }

        if (this.context === 'manage') {
            this.state.highlighted$.next(folder);
            return;
        }

        if (!folder.isSelectable) { return; }

        this.state.highlighted$.next(folder);
        this.selectedFolder.emit(folder);
    }

    public onSearchClearClicked() {
        this.searchControl.setValue('');
        this.search.nativeElement.focus();
        this.state.searchValue = '';
    }

    public navigate(key: string) {
        const [arrow, direction] = key.toLowerCase().split('arrow');
        if (arrow === '') {
            this.state.navigate(direction as any, this.vs);
        }
    }
}
